class BalanceService {
  constructor() {
    this.users = [];
    this.transactions = [];
    this.initialized = false;
    this.initializeData();
  }

  async initializeData() {
    try {
      // Load data from public folder
      const response = await fetch('/data/users.json');
      const usersData = await response.json();
      
      this.users = [...usersData.users];
      this.transactions = [...usersData.transactions];
      
      // Initialize localStorage with users data if not exists
      if (!localStorage.getItem('venmo_users')) {
        localStorage.setItem('venmo_users', JSON.stringify(this.users));
      }
      if (!localStorage.getItem('venmo_transactions')) {
        localStorage.setItem('venmo_transactions', JSON.stringify(this.transactions));
      }
      
      this.initialized = true;
    } catch (error) {
      console.error('Failed to load users data:', error);
      // Fallback to empty arrays
      this.users = [];
      this.transactions = [];
      this.initialized = true;
    }
  }

  // Update user balance
  updateUserBalance(userId, amount, type = 'deduct') {
    // Get users from localStorage to ensure we're working with latest data
    const storedUsers = localStorage.getItem('venmo_users');
    if (storedUsers) {
      this.users = JSON.parse(storedUsers);
    }

    const userIndex = this.users.findIndex(user => user.id === userId);
    if (userIndex === -1) {
      throw new Error('User not found');
    }

    const currentBalance = this.users[userIndex].balance;
    
    if (type === 'deduct') {
      if (currentBalance < amount) {
        throw new Error('Insufficient balance');
      }
      this.users[userIndex].balance = parseFloat((currentBalance - amount).toFixed(2));
    } else if (type === 'add') {
      this.users[userIndex].balance = parseFloat((currentBalance + amount).toFixed(2));
    } else if (type === 'set') {
      this.users[userIndex].balance = parseFloat(amount.toFixed(2));
    }

    // In a real app, this would persist to the backend
    // For demo purposes, we'll store in localStorage
    localStorage.setItem('venmo_users', JSON.stringify(this.users));
    
    return this.users[userIndex];
  }

  // Process payment transaction
  processPayment(senderId, receiverId, amount, note, privacy = 'friends') {
    try {
      // Validate sender has sufficient balance
      const sender = this.users.find(user => user.id === senderId);
      if (!sender) {
        throw new Error('Sender not found');
      }

      if (sender.balance < amount) {
        throw new Error('Insufficient balance');
      }

      // Deduct from sender
      this.updateUserBalance(senderId, amount, 'deduct');
      
      // Add to receiver
      this.updateUserBalance(receiverId, amount, 'add');

      // Create transaction record
      const newTransaction = {
        id: this.transactions.length + Date.now(),
        amount: amount,
        type: 'payment',
        note: note,
        sender_id: senderId,
        receiver_id: receiverId,
        status: 'completed',
        privacy: privacy,
        created_at: new Date().toISOString(),
        likes: [],
        comments: []
      };

      this.transactions.unshift(newTransaction);
      localStorage.setItem('venmo_transactions', JSON.stringify(this.transactions));

      return {
        success: true,
        transaction: newTransaction,
        updatedSender: this.users.find(user => user.id === senderId),
        updatedReceiver: this.users.find(user => user.id === receiverId)
      };

    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Process request transaction
  processRequest(senderId, receiverId, amount, note, privacy = 'friends') {
    try {
      // Create request record
      const newRequest = {
        id: this.transactions.length + Date.now(),
        amount: amount,
        type: 'request',
        note: note,
        sender_id: senderId,
        receiver_id: receiverId,
        status: 'pending',
        privacy: privacy,
        created_at: new Date().toISOString(),
        likes: [],
        comments: []
      };

      this.transactions.unshift(newRequest);
      localStorage.setItem('venmo_transactions', JSON.stringify(this.transactions));

      return {
        success: true,
        transaction: newRequest
      };

    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get current user balance
  getUserBalance(userId) {
    // First check localStorage for updated data
    const storedUsers = localStorage.getItem('venmo_users');
    if (storedUsers) {
      const users = JSON.parse(storedUsers);
      const user = users.find(u => u.id === userId);
      if (user) return user.balance;
    }

    // Fall back to original data
    const user = this.users.find(u => u.id === userId);
    return user ? user.balance : 0;
  }

  // Get all users with updated balances
  getAllUsers() {
    // If not initialized, wait for data to load from localStorage first
    if (!this.initialized) {
      const storedUsers = localStorage.getItem('venmo_users');
      if (storedUsers) {
        this.users = JSON.parse(storedUsers);
        return this.users;
      }
      return [];
    }
    
    const storedUsers = localStorage.getItem('venmo_users');
    if (storedUsers) {
      this.users = JSON.parse(storedUsers);
      return this.users;
    }
    return this.users;
  }

  // Get all transactions
  getAllTransactions() {
    const storedTransactions = localStorage.getItem('venmo_transactions');
    if (storedTransactions) {
      return JSON.parse(storedTransactions);
    }
    return this.transactions;
  }

  // Reset data to original state (for testing)
  async resetData() {
    localStorage.removeItem('venmo_users');
    localStorage.removeItem('venmo_transactions');
    
    try {
      // Reload data from public folder
      const response = await fetch('/data/users.json');
      const usersData = await response.json();
      
      this.users = [...usersData.users];
      this.transactions = [...usersData.transactions];
      
      // Reinitialize localStorage
      localStorage.setItem('venmo_users', JSON.stringify(this.users));
      localStorage.setItem('venmo_transactions', JSON.stringify(this.transactions));
    } catch (error) {
      console.error('Failed to reset data:', error);
      this.users = [];
      this.transactions = [];
    }
  }
}

// Export singleton instance
export const balanceService = new BalanceService();
export default balanceService;